/* ==========================================================================
   Provider Profile Page
   Task F13: Provider profile setup and editing
   ========================================================================== */

import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { FormField } from '@/components/ui/FormField'
import { Alert } from '@/components/ui/Alert'
import { PageContainer } from '@/components/layout/PageContainer'
import { ChangePasswordModal } from '@/components/provider/ChangePasswordModal'
import { TwoFactorSettingsCard } from '@/components/auth/TwoFactorSettingsCard'
import { PracticeAddressManager } from '@/components/forms/PracticeAddressManager'
import { useAuth, useProfileComplete } from '@/hooks/useAuth'
import { useToast } from '@/context/ToastContext'
import { usePortal, usePortalSlug } from '@/context/PortalContext'
import { US_STATES } from '@/utils/usStates'
import { CREDENTIAL_TYPES, requiresSupervisingPhysician } from '@/utils/credentials'
import {
  DEFAULT_COUNTRY,
  isCanada,
  regionLabel,
  regionOptions,
  regionPlaceholder,
} from '@/utils/countries'
import {
  updateProfile,
  listMyAddresses,
  createMyAddress,
  updateMyAddress,
  deleteMyAddress,
  getTwoFactorStatus,
  startTwoFactorSetup,
  enableTwoFactor,
  disableTwoFactor,
} from '@/api/auth'
import { ApiClientError } from '@/api/client'
import type { ProfileUpdate, CredentialType } from '@/api/types'

/* --------------------------------------------------------------------------
   NPI Validation (Luhn Algorithm)
   -------------------------------------------------------------------------- */

/**
 * Validate NPI using Luhn algorithm.
 * NPI is a 10-digit number where the checksum validates using Luhn mod 10.
 * The check digit is calculated by prepending "80840" to the 9-digit identifier.
 */
function validateNPIChecksum(npi: string): boolean {
  if (!/^\d{10}$/.test(npi)) return false

  // Prepend "80840" prefix for checksum calculation
  const prefixedNpi = '80840' + npi.slice(0, 9)
  const checkDigit = parseInt(npi[9], 10)

  // Luhn algorithm
  // Start with alternate=true because the rightmost digit of the 14-digit prefix
  // will be at position 1 when the check digit is added at position 0,
  // and position 1 should be doubled per the Luhn algorithm.
  let sum = 0
  let alternate = true

  for (let i = prefixedNpi.length - 1; i >= 0; i--) {
    let digit = parseInt(prefixedNpi[i], 10)

    if (alternate) {
      digit *= 2
      if (digit > 9) {
        digit -= 9
      }
    }

    sum += digit
    alternate = !alternate
  }

  // Check digit should make the total sum divisible by 10
  const expectedCheckDigit = (10 - (sum % 10)) % 10
  return checkDigit === expectedCheckDigit
}

/* --------------------------------------------------------------------------
   Phone Formatting
   -------------------------------------------------------------------------- */

/**
 * Format phone number as user types: (XXX) XXX-XXXX
 */
function formatPhoneNumber(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '')

  // Limit to 10 digits
  const trimmed = digits.slice(0, 10)

  // Format based on length
  if (trimmed.length === 0) return ''
  if (trimmed.length <= 3) return `(${trimmed}`
  if (trimmed.length <= 6) return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`
  return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`
}

/**
 * Strip formatting from phone number for storage.
 */
function stripPhoneFormatting(value: string): string {
  return value.replace(/\D/g, '')
}

/* --------------------------------------------------------------------------
   Validation
   -------------------------------------------------------------------------- */

interface FormErrors {
  name?: string
  npi?: string
  license_number?: string
  license_state?: string
  phone?: string
  fax?: string
  dea_number?: string
  supervising_physician_npi?: string
}

function validateForm(
  values: {
    name: string
    npi: string
    license_number: string
    license_state: string | null
    phone: string
    fax: string
    dea_number: string
    supervising_physician_npi: string
  },
  options: {
    npiRequired: boolean
    deaRequired: boolean
    phoneRequired: boolean
    usCredentials?: boolean
    /** 'State' | 'Province' — used in the license-region message (PORTAL-161) */
    licenseRegionLabel?: string
  }
): FormErrors {
  const errors: FormErrors = {}
  // NPI and DEA are US identifiers; Canadian pharmacies neither collect nor
  // validate them (PORTAL-161).
  const usCredentials = options.usCredentials ?? true

  // Name is required
  if (!values.name.trim()) {
    errors.name = 'Full name is required'
  }

  // NPI validation (conditional based on pharmacy settings)
  if (!usCredentials) {
    // skip — field is not shown
  } else if (options.npiRequired) {
    const npiDigits = values.npi.replace(/\D/g, '')
    if (!npiDigits) {
      errors.npi = 'NPI is required'
    } else if (npiDigits.length !== 10) {
      errors.npi = 'NPI must be exactly 10 digits'
    } else if (!validateNPIChecksum(npiDigits)) {
      errors.npi = 'Invalid NPI checksum'
    }
  } else {
    // If NPI is not required but provided, still validate format
    const npiDigits = values.npi.replace(/\D/g, '')
    if (npiDigits) {
      if (npiDigits.length !== 10) {
        errors.npi = 'NPI must be exactly 10 digits'
      } else if (!validateNPIChecksum(npiDigits)) {
        errors.npi = 'Invalid NPI checksum'
      }
    }
  }

  // DEA validation (conditional based on pharmacy settings)
  if (usCredentials && options.deaRequired && !values.dea_number.trim()) {
    errors.dea_number = 'DEA number is required'
  }

  // License number is required
  if (!values.license_number.trim()) {
    errors.license_number = 'License number is required'
  }

  // License state / province is required
  if (!values.license_state) {
    errors.license_state = `License ${(options.licenseRegionLabel ?? 'State').toLowerCase()} is required`
  }

  // Phone validation (required when the pharmacy requires it — PORTAL-163;
  // otherwise optional, but if provided must be valid)
  const phoneDigits = stripPhoneFormatting(values.phone)
  if (options.phoneRequired && !phoneDigits) {
    errors.phone = 'Phone number is required'
  } else if (phoneDigits && phoneDigits.length !== 10) {
    errors.phone = 'Phone number must be 10 digits'
  }

  // Fax validation (optional, but if provided must be valid)
  const faxDigits = stripPhoneFormatting(values.fax)
  if (faxDigits && faxDigits.length !== 10) {
    errors.fax = 'Fax number must be 10 digits'
  }

  // Supervising physician fields — optional for PA/NP (PORTAL-104), but the
  // NPI must still be a valid 10-digit checksummed NPI when provided
  const supNpiDigits = values.supervising_physician_npi.replace(/\D/g, '')
  if (usCredentials && supNpiDigits) {
    if (supNpiDigits.length !== 10) {
      errors.supervising_physician_npi = 'NPI must be exactly 10 digits'
    } else if (!validateNPIChecksum(supNpiDigits)) {
      errors.supervising_physician_npi = 'Invalid NPI checksum'
    }
  }

  return errors
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Profile() {
  const { user, refreshProfile, logout } = useAuth()
  const profileComplete = useProfileComplete()
  const navigate = useNavigate()
  const location = useLocation()
  const { success: showSuccess, error: showError } = useToast()
  const slug = usePortalSlug()
  const { config } = usePortal()

  // Credential requirements — prefer the per-provider effective values from the
  // authenticated profile (PORTAL-72), falling back to the public portal config.
  const npiRequired = user?.npi_required ?? config?.npi_required ?? true
  const deaRequired = user?.dea_required ?? config?.dea_required ?? false
  // Effective per-provider phone requirement (PORTAL-163)
  const phoneRequired = user?.phone_required ?? false

  // Pharmacy country (PORTAL-161). Canadian pharmacies use Province / Postal
  // Code labels and have no NPI / DEA fields at all.
  const pharmacyCountry = config?.country ?? DEFAULT_COUNTRY
  const isCanadianPharmacy = isCanada(pharmacyCountry)
  const usCredentials = !isCanadianPharmacy
  const licenseRegionLabel = regionLabel(pharmacyCountry)
  const licenseRegionOptions = regionOptions(pharmacyCountry) ?? US_STATES

  // Change password modal state
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)

  // Get return path from navigation state (or default to prescription form)
  const returnTo = (location.state as { from?: string })?.from || `/rx/${slug}/prescribe`

  // Form state
  const [name, setName] = useState('')
  const [npi, setNpi] = useState('')
  const [licenseNumber, setLicenseNumber] = useState('')
  const [licenseState, setLicenseState] = useState<string | null>(null)
  const [phone, setPhone] = useState('')
  const [fax, setFax] = useState('')

  // Addresses are handled by PracticeAddressManager below; we track the count
  // here to gate profile completion.
  const [addressCount, setAddressCount] = useState(user?.addresses?.length ?? 0)
  const addressLock = user?.address_lock ?? false
  const allowCountryField = user?.allow_country_field ?? false

  // Credential state
  const [credentialType, setCredentialType] = useState<string | null>(null)
  const [deaNumber, setDeaNumber] = useState('')
  const [supervisingPhysician, setSupervisingPhysician] = useState('')
  const [supervisingPhysicianNpi, setSupervisingPhysicianNpi] = useState('')

  // UI state
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState<string | undefined>()
  // Track if we should redirect after profile completion
  const [shouldRedirectOnComplete, setShouldRedirectOnComplete] = useState(false)

  // Determine if this is first-time setup or edit mode
  const isFirstTimeSetup = !profileComplete

  // Two-factor settings calls for this provider (PORTAL-187). Memoized so the
  // card does not refetch its status on every render.
  const userEmail = user?.email ?? ''
  const twoFactorApi = useMemo(
    () => ({
      getStatus: getTwoFactorStatus,
      startSetup: startTwoFactorSetup,
      enable: enableTwoFactor,
      disable: (code: string) => disableTwoFactor(code, userEmail),
    }),
    [userEmail]
  )

  async function handleLogout() {
    await logout()
    navigate(`/rx/${slug}/login`)
  }

  // Redirect to prescription form after profile becomes complete
  // This runs after React processes the state update from refreshProfile()
  useEffect(() => {
    if (shouldRedirectOnComplete && profileComplete) {
      navigate(returnTo, { replace: true })
    }
  }, [shouldRedirectOnComplete, profileComplete, navigate, returnTo])

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setName(user.name || '')
      setNpi(user.npi || '')
      setLicenseNumber(user.license_number || '')
      setLicenseState(user.license_state || null)
      setPhone(user.phone ? formatPhoneNumber(user.phone) : '')
      setFax(user.fax ? formatPhoneNumber(user.fax) : '')
      setAddressCount(user.addresses?.length ?? 0)
      setCredentialType(user.credential_type || null)
      setDeaNumber(user.dea_number || '')
      setSupervisingPhysician(user.supervising_physician || '')
      setSupervisingPhysicianNpi(user.supervising_physician_npi || '')
    }
  }, [user])

  // Handle field blur
  const handleBlur = (field: keyof FormErrors) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    const fieldErrors = validateForm({
      name,
      npi,
      license_number: licenseNumber,
      license_state: licenseState,
      phone,
      fax,
      dea_number: deaNumber,
      supervising_physician_npi: supervisingPhysicianNpi,
    }, { npiRequired, deaRequired, phoneRequired, usCredentials, licenseRegionLabel })
    setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] }))
  }

  // Handle phone change with formatting
  const handlePhoneChange = (value: string) => {
    setPhone(formatPhoneNumber(value))
    setApiError(undefined)
    if (touched.phone) {
      const phoneDigits = stripPhoneFormatting(value)
      if (phoneRequired && !phoneDigits) {
        setErrors((prev) => ({ ...prev, phone: 'Phone number is required' }))
      } else if (phoneDigits && phoneDigits.length !== 10) {
        setErrors((prev) => ({
          ...prev,
          phone: 'Phone number must be 10 digits',
        }))
      } else {
        setErrors((prev) => ({ ...prev, phone: undefined }))
      }
    }
  }

  // Handle fax change with formatting
  const handleFaxChange = (value: string) => {
    setFax(formatPhoneNumber(value))
    setApiError(undefined)
    if (touched.fax) {
      const faxDigits = stripPhoneFormatting(value)
      if (faxDigits && faxDigits.length !== 10) {
        setErrors((prev) => ({ ...prev, fax: 'Fax number must be 10 digits' }))
      } else {
        setErrors((prev) => ({ ...prev, fax: undefined }))
      }
    }
  }

  // Handle field change
  const handleFieldChange = (
    field: keyof FormErrors,
    value: string | null,
    setter: (value: string) => void | ((value: string | null) => void)
  ) => {
    ;(setter as (v: string) => void)(value || '')
    setApiError(undefined)
    if (touched[field]) {
      const fieldErrors = validateForm({
        name: field === 'name' ? value || '' : name,
        npi: field === 'npi' ? value || '' : npi,
        license_number:
          field === 'license_number' ? value || '' : licenseNumber,
        license_state: field === 'license_state' ? value : licenseState,
        phone: field === 'phone' ? value || '' : phone,
        fax: field === 'fax' ? value || '' : fax,
        dea_number: field === 'dea_number' ? value || '' : deaNumber,
        supervising_physician_npi:
          field === 'supervising_physician_npi'
            ? value || ''
            : supervisingPhysicianNpi,
      }, { npiRequired, deaRequired, phoneRequired, usCredentials, licenseRegionLabel })
      setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] }))
    }
  }

  // Handle form submission
  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Validate all fields
    const formErrors = validateForm({
      name,
      npi,
      license_number: licenseNumber,
      license_state: licenseState,
      phone,
      fax,
      dea_number: deaNumber,
      supervising_physician_npi: supervisingPhysicianNpi,
    }, { npiRequired, deaRequired, phoneRequired, usCredentials, licenseRegionLabel })
    setErrors(formErrors)
    setTouched({
      name: true,
      npi: true,
      license_number: true,
      license_state: true,
      phone: true,
      fax: true,
      dea_number: true,
      supervising_physician_npi: true,
    })

    // Enforce at least one practice address (unless the pharmacy is managing
    // them for the provider — in that case, the pharmacy admin is
    // responsible for ensuring one exists).
    if (addressCount === 0 && !addressLock) {
      setApiError(
        'Please add at least one practice address before saving your profile.'
      )
      return
    }

    // Check for validation errors
    const hasErrors = Object.values(formErrors).some(Boolean)
    if (hasErrors) {
      return
    }

    setLoading(true)
    setApiError(undefined)

    try {
      // Prepare update data — addresses live in their own endpoints now
      // (issues #140 / #180) and are not part of the profile update payload.
      const updateData: ProfileUpdate = {
        name: name.trim(),
        license_number: licenseNumber.trim(),
        license_state: licenseState,
        phone: stripPhoneFormatting(phone) || null,
        fax: stripPhoneFormatting(fax) || null,
        credential_type: (credentialType as CredentialType) || null,
        supervising_physician: supervisingPhysician.trim() || null,
        // US-only identifiers are not collected for Canadian pharmacies (PORTAL-161)
        ...(usCredentials
          ? {
              npi: npi.replace(/\D/g, '') || null,
              dea_number: deaNumber.trim() || null,
              supervising_physician_npi:
                supervisingPhysicianNpi.replace(/\D/g, '') || null,
            }
          : {}),
      }

      await updateProfile(updateData)
      await refreshProfile()

      showSuccess('Profile saved successfully!')

      // Redirect to prescription form after save
      // For first-time setup, use flag to wait for profileComplete state update
      // For edit mode, navigate directly since profile is already complete
      if (isFirstTimeSetup) {
        setShouldRedirectOnComplete(true)
      } else {
        navigate(returnTo, { replace: true })
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setApiError(err.getUserMessage())
        showError(err.getUserMessage())
      } else {
        const message = 'An unexpected error occurred. Please try again.'
        setApiError(message)
        showError(message)
      }
    } finally {
      setLoading(false)
    }
  }

  // Check if form has any validation errors for submit button state
  const hasValidationErrors = Object.values(errors).some(Boolean)

  return (
    <PageContainer size="md" className="py-8">
      {/* Logout button - only in edit mode */}
      {!isFirstTimeSetup && (
        <div className="flex justify-end mb-4">
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-gray-500)] hover:text-[var(--color-charcoal)] transition-colors rounded-md hover:bg-[var(--color-gray-100)]"
            aria-label="Log out"
          >
            <LogOut className="h-4 w-4" />
            <span>Log out</span>
          </button>
        </div>
      )}

      <Card>
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-serif text-2xl sm:text-3xl text-charcoal text-center mb-2">
            {isFirstTimeSetup ? 'Complete Your Profile' : 'Your Profile'}
          </h1>
          <p className="text-center text-charcoal/70">
            {isFirstTimeSetup
              ? 'Please provide your professional information to get started.'
              : 'Update your professional information below.'}
          </p>
        </div>

        {/* First-time setup alert */}
        {isFirstTimeSetup && (
          <Alert variant="info" className="mb-6">
            Please complete your profile to access the prescription form. All
            fields marked with * are required.
          </Alert>
        )}

        {/* API error alert */}
        {apiError && (
          <Alert variant="error" className="mb-6">
            {apiError}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-5">
            {/* Full Name */}
            <FormField
              label="Full Name"
              required
              error={touched.name ? errors.name : undefined}
              htmlFor="name"
            >
              <Input
                id="name"
                type="text"
                autoComplete="name"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) =>
                  handleFieldChange('name', e.target.value, setName)
                }
                onBlur={() => handleBlur('name')}
                error={touched.name && !!errors.name}
                disabled={loading}
              />
            </FormField>

            {/* NPI (US pharmacies only) */}
            {usCredentials && (
            <FormField
              label="NPI (National Provider Identifier)"
              required={npiRequired}
              error={touched.npi ? errors.npi : undefined}
              helperText="10-digit NPI number"
              htmlFor="npi"
            >
              <Input
                id="npi"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="Enter your NPI"
                value={npi}
                onChange={(e) => {
                  // Only allow digits, limit to 10
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10)
                  handleFieldChange('npi', digits, setNpi)
                }}
                onBlur={() => handleBlur('npi')}
                error={touched.npi && !!errors.npi}
                disabled={loading}
                maxLength={10}
              />
            </FormField>
            )}

            {/* License Number and State / Province (side by side on larger screens) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* License Number */}
              <FormField
                label="License Number"
                required
                error={
                  touched.license_number ? errors.license_number : undefined
                }
                htmlFor="license_number"
              >
                <Input
                  id="license_number"
                  type="text"
                  autoComplete="off"
                  placeholder="Enter license number"
                  value={licenseNumber}
                  onChange={(e) =>
                    handleFieldChange(
                      'license_number',
                      e.target.value,
                      setLicenseNumber
                    )
                  }
                  onBlur={() => handleBlur('license_number')}
                  error={touched.license_number && !!errors.license_number}
                  disabled={loading}
                />
              </FormField>

              {/* License State / Province */}
              <FormField
                label={`License ${licenseRegionLabel}`}
                required
                error={touched.license_state ? errors.license_state : undefined}
                htmlFor="license_state"
              >
                <Select
                  options={licenseRegionOptions}
                  value={licenseState}
                  onChange={(value) => {
                    setLicenseState(value)
                    setApiError(undefined)
                    if (touched.license_state) {
                      setErrors((prev) => ({
                        ...prev,
                        license_state: value
                          ? undefined
                          : `License ${licenseRegionLabel.toLowerCase()} is required`,
                      }))
                    }
                  }}
                  placeholder={regionPlaceholder(pharmacyCountry)}
                  error={touched.license_state && !!errors.license_state}
                  disabled={loading}
                />
              </FormField>
            </div>

            {/* Phone and Fax (side by side on larger screens) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Phone */}
              <FormField
                label="Phone"
                required={phoneRequired}
                error={touched.phone ? errors.phone : undefined}
                htmlFor="phone"
              >
                <Input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="(555) 555-5555"
                  value={phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onBlur={() => handleBlur('phone')}
                  error={touched.phone && !!errors.phone}
                  disabled={loading}
                />
              </FormField>

              {/* Fax */}
              <FormField
                label="Fax"
                error={touched.fax ? errors.fax : undefined}
                htmlFor="fax"
              >
                <Input
                  id="fax"
                  type="tel"
                  autoComplete="off"
                  placeholder="(555) 555-5555"
                  value={fax}
                  onChange={(e) => handleFaxChange(e.target.value)}
                  onBlur={() => handleBlur('fax')}
                  error={touched.fax && !!errors.fax}
                  disabled={loading}
                />
              </FormField>
            </div>

            {/* Practice Addresses Section (multi-address support: issues #140 / #180) */}
            <div className="border-t border-[var(--color-gray-200)] pt-5 mt-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-charcoal/80 uppercase tracking-wide">
                  Practice Addresses
                </h3>
              </div>
              <p className="text-xs text-[var(--color-gray-500)] mb-4">
                You can add multiple offices/clinics. When you write a
                prescription, you&apos;ll choose which one it&apos;s coming from.
              </p>
              <PracticeAddressManager
                handlers={{
                  list: listMyAddresses,
                  create: createMyAddress,
                  update: updateMyAddress,
                  remove: deleteMyAddress,
                }}
                readOnly={addressLock}
                lockMessage={
                  addressLock
                    ? 'Your pharmacy manages practice addresses on your behalf. Contact your pharmacy administrator to make changes.'
                    : undefined
                }
                onChange={(list) => setAddressCount(list.length)}
                showCountryField={allowCountryField}
                defaultCountry={pharmacyCountry}
              />
            </div>

            {/* Credentials Section */}
            <div className="border-t border-[var(--color-gray-200)] pt-5 mt-5">
              <h3 className="text-sm font-semibold text-charcoal/80 uppercase tracking-wide mb-4">
                Credentials
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Credential Type */}
                <FormField
                  label="Credential Type"
                  htmlFor="credential_type"
                >
                  <Select
                    options={CREDENTIAL_TYPES}
                    value={credentialType}
                    onChange={(value) => {
                      setCredentialType(value)
                      setApiError(undefined)
                      // Clear supervising physician fields when the new
                      // credential type doesn't require a supervisor (PA/NP).
                      if (!requiresSupervisingPhysician(value)) {
                        setSupervisingPhysician('')
                        setSupervisingPhysicianNpi('')
                        setErrors((prev) => ({
                          ...prev,
                          supervising_physician_npi: undefined,
                        }))
                      }
                    }}
                    placeholder="Select credential type"
                    disabled={loading}
                  />
                </FormField>

                {/* DEA Number (US pharmacies only) */}
                {usCredentials && (
                <FormField
                  label="DEA Number"
                  required={deaRequired}
                  error={touched.dea_number ? errors.dea_number : undefined}
                  htmlFor="dea_number"
                >
                  <Input
                    id="dea_number"
                    type="text"
                    autoComplete="off"
                    placeholder="Enter DEA number"
                    value={deaNumber}
                    onChange={(e) => {
                      setDeaNumber(e.target.value)
                      if (touched.dea_number && deaRequired) {
                        const val = e.target.value.trim()
                        setErrors((prev) => ({
                          ...prev,
                          dea_number: val ? undefined : 'DEA number is required',
                        }))
                      }
                    }}
                    onBlur={() => handleBlur('dea_number')}
                    error={touched.dea_number && !!errors.dea_number}
                    disabled={loading}
                    maxLength={20}
                  />
                </FormField>
                )}
              </div>

              {/* Supervising Physician (for PA/NP) */}
              {requiresSupervisingPhysician(credentialType) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                  <FormField
                    label="Supervising Physician Name"
                    htmlFor="supervising_physician"
                  >
                    <Input
                      id="supervising_physician"
                      type="text"
                      autoComplete="off"
                      placeholder="Enter supervising physician's full name"
                      value={supervisingPhysician}
                      onChange={(e) => {
                        setSupervisingPhysician(e.target.value)
                        setApiError(undefined)
                      }}
                      disabled={loading}
                      maxLength={255}
                    />
                  </FormField>
                  {usCredentials && (
                  <FormField
                    label="Supervising Physician NPI"
                    error={
                      touched.supervising_physician_npi
                        ? errors.supervising_physician_npi
                        : undefined
                    }
                    helperText="10-digit NPI number"
                    htmlFor="supervising_physician_npi"
                  >
                    <Input
                      id="supervising_physician_npi"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="Enter supervising physician's NPI"
                      value={supervisingPhysicianNpi}
                      onChange={(e) => {
                        const digits = e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 10)
                        handleFieldChange(
                          'supervising_physician_npi',
                          digits,
                          setSupervisingPhysicianNpi,
                        )
                      }}
                      onBlur={() => handleBlur('supervising_physician_npi')}
                      error={
                        touched.supervising_physician_npi &&
                        !!errors.supervising_physician_npi
                      }
                      disabled={loading}
                      maxLength={10}
                    />
                  </FormField>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Submit button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            disabled={loading || hasValidationErrors}
            className="w-full mt-8"
          >
            {isFirstTimeSetup ? 'Complete Profile' : 'Save Changes'}
          </Button>
        </form>

        {/* Cancel/Back link (only in edit mode) */}
        {!isFirstTimeSetup && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-sm text-[var(--pharmacy-primary)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pharmacy-secondary)] rounded"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        )}
      </Card>

      {/* Change Password section (only in edit mode) */}
      {!isFirstTimeSetup && (
        <Card className="mt-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-charcoal">Password</h2>
              <p className="text-sm text-charcoal/70 mt-1">
                Change your account password.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setChangePasswordOpen(true)}
            >
              Change Password
            </Button>
          </div>
        </Card>
      )}

      {!isFirstTimeSetup && <TwoFactorSettingsCard api={twoFactorApi} className="mt-6" />}

      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </PageContainer>
  )
}

export default Profile
